import { useState } from "react";
import {
  Search,
  User,
  ChevronDown,
  LogOut,
  Settings,
  Menu,
  Check,
  PanelLeft,
  Shield
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { LanguageSwitcher } from "@/components/i18n/LanguageSwitcher";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { QuickAddMenu } from "./QuickAddMenu";
import { NotificationsMenu } from "./NotificationsMenu";
import { MobileSearchModal } from "./MobileSearchModal";
import { roleConfigs, type AppRole } from "@/components/portal/RoleSwitcher";
import { useSidebar } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { isDemoUser } from "@/lib/demo";
import { DemoExitModal } from "@/components/demo/DemoExitModal";
import { PORTAL_TYPE_TO_ROLE } from "@/lib/rbac";
import { getRoleDisplayName } from "@/lib/labels";

export function AppHeader() {
  const { t } = useLanguage();
  const { profile, roles, roleAssignments, signOut, activeRole, setActiveRole } = useAuth();
  const navigate = useNavigate();
  const { toggleSidebar } = useSidebar();
  const isMobile = useIsMobile();
  const currentRole = (activeRole as AppRole) || undefined;
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);

  // Prefer the user's specific assignment label (e.g. "field_agent" → "Field Agent")
  // over the portal-level role label (e.g. "Employee"), so the avatar shows the
  // same name as the role switcher and the roles list.
  const activeAssignment = roleAssignments.find(
    (a) => PORTAL_TYPE_TO_ROLE[a.role_permissions.portal_type] === currentRole,
  );
  const activeRoleLabel = activeAssignment
    ? getRoleDisplayName(activeAssignment.role_permissions.role_name, t)
    : roleConfigs[currentRole]?.label
      ? t(roleConfigs[currentRole].label.ar, roleConfigs[currentRole].label.en)
      : t("مدير العقارات", "Property Manager");

  const handleSignOut = async () => {
    if (isDemoUser(profile?.phone)) {
      setShowExitModal(true);
      return;
    }
    await signOut();
    toast.success(t("تم تسجيل الخروج بنجاح", "Logged out successfully"));
    navigate("/auth");
  };

  const handleRoleChange = (role: AppRole) => {
    setActiveRole(role);
    if (role === "super_admin") {
      navigate("/super-admin");
      return;
    }
    const config = roleConfigs[role];
    if (config) {
      navigate(config.path === "/" ? "/dashboard" : config.path);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
      <header className="h-16 bg-card border-b border-border px-4 sm:px-6 flex items-center justify-between sticky top-0 z-40">
        {/* Unified Sidebar Toggle */}
        <Button 
          variant="ghost" 
          size="icon"
          onClick={toggleSidebar}
          className="me-2"
        >
          {isMobile ? <Menu className="h-5 w-5" /> : <PanelLeft className="h-5 w-5" />}
        </Button>

        {/* Mobile Search Button - visible only on mobile/tablet */}
        <Button 
          variant="ghost" 
          size="icon" 
          className="lg:hidden"
          onClick={() => setIsSearchOpen(true)}
        >
          <Search className="h-5 w-5" />
        </Button>

        {/* Search - hidden on mobile/tablet, shown on lg+ */}
        <div className="hidden lg:flex flex-1 max-w-md">
          <div className="relative w-full">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder={t("بحث سريع...", "Quick search...")}
              className="ps-10 bg-background border-border"
            />
          </div>
        </div>

        {/* Spacer for mobile */}
        <div className="flex-1 lg:hidden" />

        {/* Actions */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Quick Add Menu */}
          <QuickAddMenu />

          {/* Notifications */}
          <NotificationsMenu />

          {/* Language switcher — the shared full-locale dropdown (NOT a binary
              ar/en toggle): in-app must offer every enabled language and respect
              a landing-page selection (e.g. es). */}
          <LanguageSwitcher variant="outline" />


          {/* User Menu with Role Switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2 px-2" data-testid="user-menu-button">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={profile?.avatar_url || ""} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                    {profile?.full_name ? getInitials(profile.full_name) : "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden md:flex flex-col items-start">
                  <span className="text-sm font-medium">
                    {profile?.full_name || t("مستخدم", "User")}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {activeRoleLabel}
                  </span>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground hidden md:block" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-popover">
              {/* Role Switcher Section - Only show if user has multiple roles or is super_admin */}
              {(roles.length > 1 || roles.some(r => r.role === "super_admin")) && (
                <>
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                    {t("تبديل الدور", "Switch Role")}
                  </div>
                  {/* Show "Switch to Super Admin" when a super admin is on dashboard */}
                  {roles.some(r => r.role === "super_admin") && currentRole !== "super_admin" && (
                    <DropdownMenuItem
                      onClick={() => handleRoleChange("super_admin")}
                      className="gap-2 cursor-pointer"
                    >
                      <Badge className="bg-destructive text-destructive-foreground">
                        <Shield className="h-3 w-3 me-1" />
                        {t("مدير النظام", "Super Admin")}
                      </Badge>
                    </DropdownMenuItem>
                  )}
                  {roles.map((role) => {
                    if (role.role === "super_admin") return null;
                    const config = roleConfigs[role.role as AppRole];
                    if (!config) return null;
                    const Icon = config.icon;
                    const isActive = currentRole === role.role;
                    return (
                      <DropdownMenuItem
                        key={role.role}
                        onClick={() => handleRoleChange(role.role as AppRole)}
                        className={cn("gap-2 cursor-pointer", isActive && "bg-accent")}
                      >
                        <Badge className={`${config.color} text-white`}>
                          <Icon className="h-3 w-3 me-1" />
                          {t(config.label.ar, config.label.en)}
                        </Badge>
                        {isActive && (
                          <Check className="h-4 w-4 ms-auto text-primary" />
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                  <DropdownMenuSeparator />
                </>
              )}
              
              <DropdownMenuItem onClick={() => navigate("/dashboard/profile")} className="cursor-pointer">
                <User className="h-4 w-4 me-2" />
                {t("الملف الشخصي", "Profile")}
              </DropdownMenuItem>
              {/* DEPRECATED: self_admin subscription menu removed March 2026 */}
              <DropdownMenuItem onClick={() => navigate("/dashboard/settings")} className="cursor-pointer">
                <Settings className="h-4 w-4 me-2" />
                {t("الإعدادات", "Settings")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut} className="text-destructive cursor-pointer">
                <LogOut className="h-4 w-4 me-2 rtl:rotate-180" />
                {t("تسجيل الخروج", "Logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile Search Modal */}
      <MobileSearchModal 
        isOpen={isSearchOpen} 
        onClose={() => setIsSearchOpen(false)} 
      />
      <DemoExitModal open={showExitModal} onOpenChange={setShowExitModal} />
    </>
  );
}
